// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LightRoundedIcon from '@mui/icons-material/LightRounded';

// material ui
import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';

// api
import type { GetDeviceDto } from '@/api/generated/recalc/endpoints/v2/devices';

// shared components
import { WritableOnly } from '@/components/access-control/AccessGuard';
import StyledCustomLink from '@/components/link/StyledCustomLink';

// feature-components
import CopyableIdField from '@/features/devices/components/shared/CopyableIdField';
import DetailHeaderCard from '@/features/devices/components/shared/DetailHeaderCard';
import DetailInfoField from '@/features/devices/components/shared/DetailInfoField';
import useCopyToClipboard from '@/features/devices/components/shared/hooks/useCopyToClipboard';

// relative
import {
  formatDeviceLocation,
  formatDeviceTag,
  formatDeviceTypeLabel,
  getDeviceDisplayName,
} from './devices.utils';
import DeviceStatusCell from './DeviceStatusCell';
import DeviceTagChip from './DeviceTagChip';

interface DeviceDetailHeaderProps {
  accountId: number;
  device?: GetDeviceDto | null;
  onEditGeneralInformation?: () => void;
}

const DeviceDetailHeader = ({
  accountId,
  device,
  onEditGeneralInformation,
}: DeviceDetailHeaderProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const copyToClipboard = useCopyToClipboard();

  const handleCopyId = () =>
    copyToClipboard(
      String(device?.deviceId ?? ''),
      t('devices.deviceIdCopied', { id: device?.deviceId }),
      t('devices.deviceIdCopyFailed'),
    );

  const mappedUnits = Array.from(
    new Map(
      (device?.mappedUnits ?? [])
        .filter((unit) => unit.unitId)
        .map((unit) => [unit.unitId, unit]),
    ).values(),
  );

  return (
    <DetailHeaderCard
      title={getDeviceDisplayName(device) || t('common.notAvailable')}
      subheader={formatDeviceTypeLabel(device?.deviceType)}
      avatar={<DeviceStatusCell hideLabel={true} device={device} />}
      action={
        onEditGeneralInformation ? (
          <WritableOnly>
            <Tooltip title={t('devices.editGeneralInformation')}>
              <IconButton onClick={onEditGeneralInformation} size="small">
                <EditRoundedIcon />
              </IconButton>
            </Tooltip>
          </WritableOnly>
        ) : null
      }
      gap={2}
    >
      <DetailInfoField label={t('devices.deviceId')}>
        <CopyableIdField
          value={String(device?.deviceId ?? '')}
          notAvailableText={t('common.notAvailable')}
          onCopy={handleCopyId}
        />
      </DetailInfoField>

      <DetailInfoField label={t('devices.deviceType')}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {formatDeviceTypeLabel(device?.deviceType) ||
            t('common.notAvailable')}
        </Typography>
      </DetailInfoField>

      <DetailInfoField label={t('devices.description')}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {device?.description?.trim() || t('common.notAvailable')}
        </Typography>
      </DetailInfoField>

      <DetailInfoField label={t('devices.location')}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {formatDeviceLocation(device?.location) || t('devices.noLocation')}
        </Typography>
      </DetailInfoField>

      <DetailInfoField label={t('devices.tags')}>
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {device?.tags?.length ? (
            device.tags.map((tag, index) => {
              const label = formatDeviceTag(tag);

              return (
                <DeviceTagChip
                  key={`${tag?.key ?? 'tag'}-${tag?.value ?? index}`}
                  label={label || t('common.notAvailable')}
                />
              );
            })
          ) : (
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {t('common.notAvailable')}
            </Typography>
          )}
        </Stack>
      </DetailInfoField>

      <DetailInfoField label={t('sensors.meteredUnits')}>
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {mappedUnits.length ? (
            mappedUnits.map((unit) => (
              <StyledCustomLink
                key={unit.unitId}
                to="/$accountId/units/$unitId"
                params={{
                  accountId: String(accountId),
                  unitId: String(unit.unitId),
                }}
              >
                <Chip
                  icon={<LightRoundedIcon sx={{ color: 'white !important' }} />}
                  label={`${unit.unitName || t('common.notAvailable')} (${unit.unitId})`}
                  color="info"
                  size="small"
                  clickable
                  sx={{
                    color: 'white',
                    '& .MuiChip-label': { color: 'white' },
                    '&:hover': { opacity: 0.9 },
                  }}
                />
              </StyledCustomLink>
            ))
          ) : (
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {t('common.notAvailable')}
            </Typography>
          )}
        </Stack>
      </DetailInfoField>
    </DetailHeaderCard>
  );
};

export default DeviceDetailHeader;
